<template>
    <component
        :is="to ? DynamicLink : 'div'"
        class="a-value-card"
        :to="to ? to : null"
        :class="className"
    >
        <div
            v-if="image || media"
            class="image-wrapper"
            :class="pictureClassName"
        >
            <a-picture
                v-if="image"
                class="image-container"
                :link="image"
                :alt="imageAlt || title"
                :fit="imageFit"
            />
            <a-media
                v-if="media"
                class="media"
                v-bind="media"
                :alt="media.alt || media.imageAlt || title"
            />
        </div>
        <div class="content">
            <a-label v-if="label" v-bind="label">
                {{ label?.text }}
            </a-label>
            <div class="header">
                <component :is="titleTagName" v-if="title" class="title">
                    {{ title }}
                </component>
                <a-icon
                    v-if="icon"
                    class="icon"
                    :number="icon"
                    :alt="iconAlt || title"
                />
            </div>
            <div
                v-if="text"
                class="description"
                :class="{'has-button': ctaButtons?.length}"
            >
                <a-dangerous-html :content="text" />
            </div>

            <div v-if="ctaButtons" class="buttons">
                <a-button
                    v-for="(button, index) in ctaButtons"
                    :key="index"
                    v-bind="button"
                />
            </div>
        </div>
    </component>
</template>

<script setup>
import { computed } from 'vue';
import AButton from '@core/components/button/button.vue';
import { BORDER_RADIUS } from '@core/components/button/constants.js';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import DynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import AIcon from '@core/components/icon/icon.vue';
import ALabel from '@core/components/label/label.vue';
import AMedia from '@core/components/media/media.vue';
import { FIT_TYPES } from '@core/components/picture/constants.js';
import APicture from '@core/components/picture/picture.vue';
import { COLORS } from '@model/slices/basic-slice.js';

const DARK_BACKGROUNDS = [
    'dark-blue',
    'd-b-gradient-right',
    'd-b-gradient-bottom',
    'n-p-gradient-top',
    'navy-gradient',
];

defineOptions({
    name: 'AValueCard',
    inheritAttrs: false,
});

const props = defineProps({
    to: {
        type: String,
        default: undefined,
    },
    media: {
        type: Object,
        default: undefined,
    },
    label: {
        type: Object,
        default: undefined,
    },
    title: {
        type: String,
        default: undefined,
    },
    text: {
        type: String,
        default: undefined,
    },
    titleTag: {
        type: String,
        default: 'div',
    },
    ctaButtons: {
        type: Array,
        default: null,
    },
    borderRadius: {
        type: Number,
        default: 4,
        validator: (value) => !value || BORDER_RADIUS.includes(value),
    },
    centered: {
        type: Boolean,
        default: false,
    },
    background: {
        type: String,
        default: undefined,
        validator: (value) => value === undefined || COLORS.includes(value),
    },

    /* Left image and icons props for backward compatibility */
    image: {
        type: String,
        default: undefined,
    },
    imageAlt: {
        type: String,
        default: undefined,
    },
    imageFit: {
        type: String,
        default: 'cover',
        validator: (val) => FIT_TYPES.includes(val),
    },
    icon: {
        type: [String, Number],
        default: '',
    },
    iconAlt: {
        type: String,
        default: '',
    },
});

const titleTagName = computed(() => props.titleTag);

const className = computed(() => [
    `a-value-card_radius_${props.borderRadius}`,
    {
        'a-value-card_background': props.background,
        [`a-value-card_background_${props.background}`]: props.background,
    },
    { 'is-centered': props.centered },
    { 'is-dark': DARK_BACKGROUNDS.includes(props.background) },
    { linked: props.to },
    { 'with-image': Boolean(props.image) },
]);

const pictureClassName = computed(() => [
    { 'has-contain-fit': props.imageFit === 'contain' },
]);
</script>

<style lang="pcss" scoped>
.a-value-card {
    display: flex;
    flex-flow: column;
    width: 100%;
    height: 100%;
    border: 1px solid var(--av-brand-secondary-light);
    border-radius: var(--a-value-card-border-radius);
    box-shadow: var(--av-shadow-small);
    overflow: hidden;

    :deep(.media-wrapper) {
        .a-media__type__screenshot {
            border-radius: 0;
        }
        .screenshot-image,
        .a-video-card {
            border-radius: 0;
            border: none;
            box-shadow: none;
            outline: none;
        }
        &.has-outline {
            &:focus-within,
            &:focus-visible {
                .media {
                    outline: none;
                    outline-offset: 0;
                    border-radius: 0;
                }
            }
        }
    }

    .image-wrapper {
        aspect-ratio: 16 / 9;
        background: var(--av-inversed-primary);
        &.has-contain-fit {
            aspect-ratio: unset;
            height: auto;
            max-height: 246px;
            .image-container {
                max-height: 246px;
                overflow: hidden;
            }
            :deep(.media-wrapper) {
                max-height: 246px;
                overflow: hidden;
            }
        }
        :deep(.a-picture__img) {
            width: 100%;
        }
    }

    .content {
        display: flex;
        flex-flow: column;
        align-items: flex-start;
        background: var(--av-inversed-primary);
        padding: 24px;
        height: 100%;
        z-index: 2;
        :deep(.a-label) {
            margin-bottom: 16px;
        }
        .header {
            margin-bottom: 16px;
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            .title {
                @mixin title-accent;
                color: var(--av-nav-primary);
            }
            .icon {
                margin-inline-start: 24px;
                width: 64px;
                height: 64px;
            }
        }
        .description {
            @mixin paragraph;
            color: var(--av-fixed-secondary);
            &.has-button {
                margin-bottom: 24px;
            }
        }
        .buttons {
            width: 100%;
            display: flex;
            flex-flow: column;
            gap: 16px;
            margin-top: auto;
            :deep(.a-button) {
                text-align: center;
                justify-content: center;
            }
        }

    }

    &_background {
        @mixin background;

        .content {
            background: transparent;
        }
    }

    &.is-centered {
        text-align: center;
        .content {
            align-items: center;
        }
    }

    &.is-dark {
        .content {
            .header .title,
            .description {
                color: var(--av-inversed-primary);
            }

            .description :deep(a) {
                color: var(--av-link-inversed);
            }
        }
    }

    &_radius {
        &_0 { --a-value-card-border-radius: 0px }
        &_2 { --a-value-card-border-radius: 2px }
        &_4 { --a-value-card-border-radius: 4px; }
        &_8 { --a-value-card-border-radius: 8px; }
        &_12 { --a-value-card-border-radius: 12px; }
        &_16 { --a-value-card-border-radius: 16px; }
        &_20 { --a-value-card-border-radius: 20px; }
        &_24 { --a-value-card-border-radius: 24px; }
        &_28 { --a-value-card-border-radius: 28px; }
    }

    &.linked {
        .content {
            .header {
                .title {
                    color: var(--av-brand-secondary);
                    transition: color 0.15s ease-out;
                }
            }
        }
        &:hover {
            box-shadow: none;
            .content {
                .header {
                    .title {
                        color: var(--av-nav-secondary);
                    }
                }
            }
        }
    }
}
</style>
